import React, { useState, useEffect } from 'react';
import { BillData, PaymentRecord, Company } from '../types/billing';
import { formatAmount } from '../utils/formatters';

interface BillManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  bill: BillData | null;
  companies: Company[];
  onUpdateBill: (bill: BillData) => Promise<void>;
  onAddPayment: (payment: Omit<PaymentRecord, 'id'>) => Promise<void>;
  onUpdatePayment: (payment: PaymentRecord) => Promise<void>;
  onDeletePayment: (paymentId: string) => Promise<void>;
  payments: PaymentRecord[];
}

const BillManagementModal: React.FC<BillManagementModalProps> = ({
  isOpen,
  onClose,
  bill,
  companies,
  onUpdateBill,
  onAddPayment,
  onUpdatePayment,
  onDeletePayment,
  payments
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState<PaymentRecord | null>(null);
  const [paymentForm, setPaymentForm] = useState<{
    amount: string;
    date: string;
    mode: 'NEFT' | 'IMPS' | 'RTGS' | 'Cash' | 'Cheque' | 'UPI' | 'Other';
    reference: string;
    notes: string;
  }>({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    mode: 'NEFT',
    reference: '',
    notes: ''
  });

  // Get payments for this bill
  const billPayments = bill ? payments.filter(p => p.billId === bill.id) : [];
  const totalPaid = billPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const remainingAmount = bill ? bill.totalAmount - totalPaid : 0;
  const isFullyPaid = remainingAmount <= 0;

  useEffect(() => {
    if (editingPayment) {
      setPaymentForm({
        amount: editingPayment.amount.toString(),
        date: editingPayment.date.split('T')[0],
        mode: editingPayment.mode,
        reference: editingPayment.reference || '',
        notes: editingPayment.notes || ''
      });
    } else {
      setPaymentForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        mode: 'NEFT',
        reference: '',
        notes: ''
      });
    }
  }, [editingPayment]);

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bill) return;

    setIsLoading(true);
    try {
      const paymentData = {
        companyId: bill.clientId,
        billId: bill.id,
        amount: parseFloat(paymentForm.amount),
        date: new Date(paymentForm.date).toISOString(),
        mode: paymentForm.mode,
        reference: paymentForm.reference,
        notes: paymentForm.notes
      };

      if (editingPayment) {
        await onUpdatePayment({ ...paymentData, id: editingPayment.id });
      } else {
        await onAddPayment(paymentData);
      }

      setShowPaymentForm(false);
      setEditingPayment(null);
      setPaymentForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        mode: 'NEFT',
        reference: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error saving payment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettleBill = async () => {
    if (!bill || remainingAmount <= 0) return;

    setIsLoading(true);
    try {
      // Create a payment for the remaining amount
      const paymentData = {
        companyId: bill.clientId,
        billId: bill.id,
        amount: remainingAmount,
        date: new Date().toISOString(),
        mode: 'NEFT' as const,
        reference: `SETTLE-${bill.invoiceNumber}`,
        notes: 'Bill settled - remaining amount'
      };

      await onAddPayment(paymentData);
      
      // Update bill status to Paid
      await onUpdateBill({
        ...bill,
        status: 'Paid',
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error settling bill:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await onDeletePayment(paymentId);
      } catch (error) {
        console.error('Error deleting payment:', error);
      }
    }
  };

  if (!isOpen || !bill) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Manage Bill</h2>
              <p className="text-gray-600">Invoice #{bill.invoiceNumber} - {bill.clientName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Bill Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm font-medium text-blue-800">Total Amount</div>
              <div className="text-2xl font-bold text-blue-900">{formatAmount(bill.totalAmount)}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm font-medium text-green-800">Amount Paid</div>
              <div className="text-2xl font-bold text-green-900">{formatAmount(totalPaid)}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm font-medium text-red-800">Remaining</div>
              <div className="text-2xl font-bold text-red-900">{formatAmount(remainingAmount)}</div>
            </div>
            <div className={`rounded-lg p-4 ${isFullyPaid ? 'bg-green-50' : 'bg-yellow-50'}`}>
              <div className={`text-sm font-medium ${isFullyPaid ? 'text-green-800' : 'text-yellow-800'}`}>Status</div>
              <div className={`text-lg font-bold ${isFullyPaid ? 'text-green-900' : 'text-yellow-900'}`}>
                {isFullyPaid ? 'Fully Paid' : 'Pending'}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 mb-6">
            <button
              onClick={() => setShowPaymentForm(true)}
              className="bg-avis-blue text-white px-4 py-2 rounded-md hover:bg-avis-dark transition-colors"
            >
              Add Payment
            </button>
            {remainingAmount > 0 && (
              <button
                onClick={handleSettleBill}
                disabled={isLoading}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Settling...' : 'Settle Bill'}
              </button>
            )}
            <button
              onClick={() => onUpdateBill({ ...bill, status: 'Paid', updatedAt: new Date().toISOString() })}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
            >
              Mark as Paid
            </button>
          </div>

          {/* Payment Form */}
          {showPaymentForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingPayment ? 'Edit Payment' : 'Add New Payment'}
                </h3>
                <button
                  onClick={() => {
                    setShowPaymentForm(false);
                    setEditingPayment(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handlePaymentSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                  </label>
                  <input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                    step="0.01"
                    min="0"
                    max={remainingAmount}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={paymentForm.date}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Mode *
                  </label>
                  <select
                    value={paymentForm.mode}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, mode: e.target.value as 'NEFT' | 'IMPS' | 'RTGS' | 'Cash' | 'Cheque' | 'UPI' | 'Other' }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                    required
                  >
                    <option value="NEFT">NEFT</option>
                    <option value="IMPS">IMPS</option>
                    <option value="RTGS">RTGS</option>
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                    <option value="UPI">UPI</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference
                  </label>
                  <input
                    type="text"
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                    placeholder="Transaction reference..."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                    rows={2}
                    placeholder="Payment notes..."
                  />
                </div>

                <div className="md:col-span-2 flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPaymentForm(false);
                      setEditingPayment(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-avis-blue text-white rounded-md hover:bg-avis-dark transition-colors disabled:opacity-50"
                  >
                    {isLoading ? 'Saving...' : editingPayment ? 'Update Payment' : 'Add Payment'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Payments List */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h3>
            {billPayments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No payments recorded yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mode
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reference
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {billPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(payment.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatAmount(payment.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.mode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.reference || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {payment.notes || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingPayment(payment);
                                setShowPaymentForm(true);
                              }}
                              className="text-avis-blue hover:text-avis-dark"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeletePayment(payment.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillManagementModal;
